import { Timeout, dateFormatter, generateBackgroundImgUrl, setStylesOnElement } from "../../util/Misc";
import { Constants } from "../../model/Constants";
import {customersimpleviewRender} from "../templates/renders/customersimpleview";

/**
 * Creates the customer-simple-view custom element which will be the container for customer-bubble element,
 * This element handles the margin between bubble elements , also when grouping is enabled all adjacent customer-bubbles are rendered within this element
 * @type {{template, insertBubble(*=): void, created(): void, updateStyleProps(*): void, tagName: string, addBubble(*, *=): void, render(*=): void, setSpeechImage(): void}}
 */
const CustomerSimpleView: any = {
    tagName: "customer-simple-view",
    initialize(options) {
        initComponent(options);
    },
    renderer: customersimpleviewRender,
    created() {
        this.container = this.el.querySelector(".bubble-container");
        this.childCount = 0;
        this.custNameEl = this.el.querySelector(".msg-info-name span");
        this.timeEl = this.el.querySelector(".msg-info-time span");
        this.msgInfo = this.el.querySelector(".msg-info.below");
        this.lastChid = null;
    },
    getContainer() {
        return this.container;
    },

    /**
     * set the this element styles
     */
    updateStyleProps() {
        setStylesOnElement(this.bubbleStyleProps, this.el.firstChild);
    },

    /**
     * set the styles and also create and render the child customer-bubble element
     * @param {Message} message
     */
    render(message) {
        this.updateStyleProps(message);
        this.addBubble(message);

        // set customer name value in info element, this element will be found only if the customer Name is configured to display out side customer-bubble element
        if (this.custNameEl) {
            this.custNameEl.innerText = message.getName();
        }

        // set time value  in info element, this element will be found only if the time is configured to display out side customer-bubble element
        if (this.timeEl) {
            this.timeEl.innerText = dateFormatter(parseInt(message.getTimeStamp(), 10), this.format);
        }
    },

    /**
     * Set speech image and position if speech view is enabled
     */
    setSpeechImage(message) {
        if (this.speechView) {
            this.speechCon = this.el.firstChild.querySelector(".speechCont");
            const img = this.speechCon.firstElementChild;

            switch (this.speechPos) {
                case "bottom":
                    img.classList.add("bottom");
                    break;

                case "center":
                    img.classList.add("center");
                    break;

                // no default
            }

            let imgURL;

            if (this.customerSpeechConfig) {
                imgURL = generateBackgroundImgUrl(this.customerSpeechConfig, this.$app);
            }

            if (imgURL) {
                img.style.backgroundImage = imgURL;
            } else if (this.customerSpeechConfig.customerColors && this.customerSpeechConfig.customerColors.length > 0) {
                img.style.backgroundColor = this.customerSpeechConfig.customerColors[0];

                if (this.customerSpeechConfig.customerInitial) {
                    img.innerHTML = ["<span>", message.getName()[0], "</span>"].join("");
                }
            }
        }
    },

    /**
     * Insert the child customer-bubble as first child in this element
     * @param {Message} message
     */
    insertBubble(message) {
        this.addBubble(message, true);
    },

    /**
     * add the child customer-bubble as last child in this element
     * @param {Message} message
     * @param {boolean} previous
     */
    addBubble(message, previous) {
        if (message.getIsRestore()) {
            this.paintBubble(message, previous);
        } else {
            window.raf(() => {
                this.paintBubble(message, previous);
            });
        }
    },
    paintBubble(message, previous) {
        let bubble;
        bubble = document.createElement("customer-bubble");

        if (this.childCount === 0) {
            this.setSpeechImage(message);
        }

        /**
         * if not restoring then remove the animation class for info element before rendering child element
         */
        if (!message.getIsRestore()) {
            if (this.msgInfo) {
                this.msgInfo.classList.remove("enter");
            }

            if (this.speechCon) {
                this.speechCon.classList.remove("enter");
            }
        }

        if (bubble) {
            if (message.getRichData() == null) {
                bubble.setAttribute("messagetext", message.getMessageText());
            }

            bubble.setAttribute("name", message.getName());
            bubble.setAttribute("timestamp", message.getTimeStamp());
            bubble.setAttribute("restore", message.getIsRestore());

            if (previous && this.childCount > 0) {
                this.container.insertBefore(bubble, this.container.firstElementChild);
                bubble.hideBottomInfo();

                if (this.lastChid) {
                    this.lastChid.hideTopInfo();
                }
            } else {
                this.container.appendChild(bubble);

                if (this.childCount > 0) {
                    if (this.lastChid) {
                        this.lastChid.hideBottomInfo();
                    }

                    bubble.hideTopInfo();
                }
            }

            if (message.getRichData() != null) {
                const insta = bubble.setRichObject(message, message.getRichData());
                message.setRichInstance(insta);
            }
        }

        /**
         * set the grouped agent-bubble element bottom or top margin
         */

        if (previous && this.childCount > 0) {
            bubble.firstChild.style.marginBottom = `${this.bubbleStyleProps.groupedMargin}px`;
        } else if (this.childCount > 0) {
            bubble.firstChild.style.marginTop = `${this.bubbleStyleProps.groupedMargin}px`;
        }

        /**
         * if not restoring then add the animation class for info element after rendering child element
         */

        if (!message.getIsRestore()) {
            Timeout.set("infotimer", () => {
                if (this.speechCon) {
                    this.speechCon.classList.add("enter");
                }

                if (this.msgInfo) {
                    this.msgInfo.classList.add("enter");
                }
            }, 25);
        }

        this.childCount++;
        this.lastChid = bubble;
    },
};

/**
 * @export CustomerSimpleView
 * @param {CIApp} appInstance
 * @return {{template, insertBubble, (*=): void, created, (): void, updateStyleProps, (*): void, tagName: string, addBubble, (*, *=): void, render, (*=): void, setSpeechImage, (): void}}
 */
export default appInstance => {
    CustomerSimpleView.$app = appInstance;
    const constObj: any = {};
    CustomerSimpleView.constants = constObj;
    return CustomerSimpleView;
};

const initComponent = (options: any) => {
    const appInstance = options.$app;
    const config = appInstance.getUIConfig();
    const constObj: any = options.constants;

    const styleConfig: any = {};
    let bubbleStyleProps;

    if (config.transcriptConfig) {
        const tConfig = config.transcriptConfig;
        bubbleStyleProps = tConfig.bubbleStyleProperties || {};
        constObj.nameStyle = tConfig.customerNameStyles;
        constObj.timeStyle = tConfig.dateTimeStyles;
        constObj.textAlignment = tConfig.customerTextAlignment || "right";
        constObj.customerSpeechConfig = tConfig.customerSpeechAvatarConfig;
        constObj.avatarStyle = tConfig.speechAvatarStyles;
    } else {
        bubbleStyleProps = {};
    }

    styleConfig.marginLeft = `${bubbleStyleProps.customerBubbleLeftMargin || 100}px`;
    styleConfig.marginRight = `${bubbleStyleProps.customerBubbleRightMargin || 3}px`;
    styleConfig.marginBottom = `${bubbleStyleProps.customerBubbleBottomMargin || 15}px`;
    styleConfig.groupedMargin = bubbleStyleProps.groupedBubbleMargin || 5;

    constObj.bubbleStyleProps = styleConfig;

    const appConfig = config.appConfigProps;

    if (appConfig) {
        constObj.showName = appConfig.displayCustomerName;
        constObj.namePos = appConfig.customerNamePosition;
        constObj.showTime = appConfig.displayDateAndTime;
        constObj.timePos = appConfig.dateTimePosition;
        constObj.format = appConfig.timeStampFormat || Constants.DEFAULT_TIMESTAMP_FORMAT;
        constObj.speechView = appConfig.setCustomerSpeechView;
        constObj.speechPos = appConfig.speechImgPosition;
    }
};
